import { useCallback, useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Tooltip,
    Stepper,
    Step,
    StepLabel,
} from '@mui/material';

import FieldNames from '../../../defaults/FieldNames.json';
import { useSelector } from 'react-redux';
import ColorConstants from '../../../defaults/ColorConstants';
import { getAppConfig} from '../../../utils/ReduxHelper';

import {
    recalcProportionalFundingAcres,
} from '../../../utils/FundingHelper';
import ExistingSubstitutionFeatureSearch from '../ExistingSubstitutionFeatureSearch/ExistingSubstitutionFeatureSearch.tsx';
import SubstitutionManageFunding from '../SubstitutionManageFunding/SubstitutionManageFunding.tsx';
import {padding} from "@mui/system";

interface SubstituteExistingActionProps {
    open: boolean;
    onClose: () => void;
    onSave: (
        selectedActionId: number,
        selectedFundingIds: number[],
        updatedFunding: any[],
        remainingFundingRows: any[],
        selectedAction: any
    ) => void;
    joinId: string;
    joinFeature: string;
}

const FF = FieldNames.fundingFields;
const FeatFields = FieldNames.featureFields;

export default function SubstituteExistingAction({
    open,
    onClose,
    onSave,
    joinId,
    joinFeature,
}: SubstituteExistingActionProps) {
    const config: any = getAppConfig();
    const feature = useSelector((state: any) => state.details.feature);
    const isPostFire = feature?.attributes?.[FeatFields.entityType] === 'PostFire';
    // stepper handling
    const steps = [
        'Select Existing Action for Substitution',
        'Edit Funding Lines for Substitution',
    ];

    const [activeStep, setActiveStep] = useState(0);
    const stepForward = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const stepBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const resetStepAndClose = () => {
        setActiveStep(0);
        onClose();
    };
    const [actions, setActions] = useState<any[]>([]);
    const [totalsMatch, setTotalsMatch] = useState<boolean>(false);

    const [selectedActionId, setSelectedActionId] = useState<number | null>(null);

    // Funding management - these represent the ORIGIN feature's (the action
    // being substituted) funding lines, which the user selects/edits to
    // decide how much moves onto the selected existing action.

    const [fundingRows, setFundingRows] = useState<any[]>([]);
    const [initFundingRows, setInitFundingRows] = useState<any[]>([]);
    // Rows representing the funding that stays behind on the origin feature.
    // Seeded at $0 for every cost field - the user must manually move
    // amounts here (out of the "moved" grid above) so the grand total across
    // both grids still equals the original funding total. Nothing is
    // redistributed/subtracted automatically.
    const [remainingFundingRows, setRemainingFundingRows] = useState<any[]>([]);
    const [selectedFundingIds, setSelectedFundingIds] = useState<any>({
        ids: new Set(),
        type: 'include',
    });

    const [selectedActionCalculatedArea, setSelectedActionCalculatedArea] = useState<number>(0);

    if (!config) return null;

    // Track the selected (target) action's calculated area - needed to
    // proportionally reallocate Acres across the moved funding lines.
    useEffect(() => {
        if (selectedActionId) {
            const selectedAction = actions.find((a) => a[FeatFields.objectID] === selectedActionId);
            setSelectedActionCalculatedArea(selectedAction?.[FeatFields.calculatedArea] || 0);
        } else {
            setSelectedActionCalculatedArea(0);
        }
    }, [selectedActionId, actions]);

    // Whenever the target action (and therefore its calculated area) changes,
    // immediately reallocate Acres proportionally across the current funding
    // rows - so even if the user never edits a single cell, the default
    // "move everything" state still reflects correct Acres for the target
    // feature (rather than the origin feature's Acres split).
    useEffect(() => {
        if (selectedActionCalculatedArea > 0) {
            setFundingRows((prevRows) =>
                prevRows.length > 0
                    ? recalcProportionalFundingAcres(
                          prevRows,
                          selectedActionCalculatedArea,
                          isPostFire
                      )
                    : prevRows
            );
        }
    }, [selectedActionCalculatedArea, isPostFire]);

    const handleSave = useCallback(() => {
        if (selectedActionId) {
            const selectedIds = Array.from(selectedFundingIds.ids).map(Number);
            const selectedAction = actions.find((a) => a[FeatFields.objectID] === selectedActionId);

            // Only rows the user has selected actually move to the target existing action.
            const movedFundingRows = fundingRows.filter((row) =>
                selectedIds.includes(Number(row[FF.id] ?? row[FF.objectID]))
            );
            const originCalculatedArea = feature?.attributes?.[FeatFields.calculatedArea] || 0;
            const finalRemainingFundingRows = recalcProportionalFundingAcres(
                remainingFundingRows,
                originCalculatedArea,
                isPostFire
            );

            onSave(
                selectedActionId,
                selectedIds,
                movedFundingRows,
                finalRemainingFundingRows,
                selectedAction
            );
            onClose();
        }
    }, [
        selectedActionId,
        selectedFundingIds,
        fundingRows,
        remainingFundingRows,
        feature,
        isPostFire,
        actions,
        onSave,
        onClose,
    ]);

    const getStepContent = (step: number) => {
        switch (step) {
            case 0:
                return (
                    <ExistingSubstitutionFeatureSearch
                    selectedActionId={selectedActionId}
                    setSelectedActionId={setSelectedActionId}
                    setActions={setActions}
                    isPostFire={isPostFire}
                    feature={feature}
                    open={open}
                ></ExistingSubstitutionFeatureSearch>)
            case 1:
                return (
                               <SubstitutionManageFunding
                                   joinId={joinId}
                                   joinFeature={joinFeature}
                                   fundingRows={fundingRows}
                                   setFundingRows={setFundingRows}
                                   initFundingRows={initFundingRows}
                                   setInitFundingRows={setInitFundingRows}
                                   remainingFundingRows={remainingFundingRows}
                                   setRemainingFundingRows={setRemainingFundingRows}
                                   selectedFundingIds={selectedFundingIds}
                                   setSelectedFundingIds={setSelectedFundingIds}
                                   selectedActionCalculatedArea={selectedActionCalculatedArea}
                                   open={open}
                                   setTotalsMatch={setTotalsMatch}
                               ></SubstitutionManageFunding>
                           )
            default:
                return <></>

            }

    }


    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='xl'
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        height: '90vh',
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    backgroundColor: ColorConstants.BlueAlien,
                    color: ColorConstants.WhiteAlien,
                }}
            >
                Substitution - Existing Action
            </DialogTitle>
            <DialogContent sx={{ paddingBottom: 0 }} dividers>
                <Typography variant='body2' sx={{ mb: 2, fontStyle: 'italic' }}>
                    You will be redirected to the selected existing action after clicking 'SAVE';
                    You will only be permitted to substitute an existing action that has like
                    approvals (ex. Local approval)
                </Typography>
                <Box sx={{ width: '100%', my: 2 }}>
                    <Stepper
                        activeStep={activeStep}
                        orientation='horizontal'
                        sx={{ paddingBottom: 1.5 }}
                    >
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {activeStep === steps.length ? (
                        <Box sx={{ textAlign: 'center', pt: 3 }}>
                            <Typography variant='h6'>All done!</Typography>
                            <Button sx={{ mt: 2 }}>Reset Form</Button>
                        </Box>
                    ) : (
                        // Call the content helper here
                        getStepContent(activeStep)
                    )}
                </Box>

                {/* Funding Management Section */}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant='contained' color='primary'>
                    CANCEL
                </Button>
                <Tooltip
                    title={
                        !selectedActionId
                            ? 'Please select an action'
                            : !totalsMatch
                              ? 'Funding totals must match before saving'
                              : selectedFundingIds.ids.size === 0
                                ? 'At least one funding row must be selected'
                                : ''
                    }
                >
                    <span>
                        {activeStep === steps.length ? (
                            <Button onClick={resetStepAndClose}>Reset</Button>
                        ) : (
                            <>
                                <Button
                                    disabled={activeStep === 0}
                                    onClick={stepBack}
                                    sx={{ mr: 1 }}
                                >
                                    Back
                                </Button>
                                <Button
                                    variant='contained'
                                    onClick={
                                        activeStep === steps.length - 1 ? handleSave : stepForward
                                    }
                                    disabled={
                                        activeStep === steps.length - 1
                                            ? !selectedActionId ||
                                              selectedFundingIds.ids.size === 0 ||
                                              !totalsMatch
                                            : !selectedActionId
                                    }
                                >
                                    {activeStep === steps.length - 1 ? 'Save' : 'Next'}
                                </Button>
                            </>
                        )}
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
}
